"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/useAuthStore";
import { getInteraction, interactionKey, replaceInteraction, setInteraction, useInteraction } from "@/features/interactions/store";
import { syncInteractionCache } from "@/features/interactions/cache";
import { toggleLike, type LikeableModel } from "./api";
import { toastApiError } from "@/lib/api-toast";
type UseLikeToggleOptions = {
  modelType: LikeableModel;
  /** Numeric for most models; drops identify by slug (they have no numeric id). */
  modelId?: number | string;
  /** `current_user.like` from the API — never a count, never `Boolean(object)`. */
  initialLiked?: boolean;
  /** `likes.count` from the API — display only, independent of `initialLiked`. */
  initialCount?: number;
  errorMessage?: string;
};
export function useLikeToggle({
  modelType,
  modelId,
  initialLiked = false,
  initialCount = 0,
  errorMessage
}: UseLikeToggleOptions) {
  const {
    user,
    openLoginModal
  } = useAuthStore();
  const queryClient = useQueryClient();
  const key = interactionKey(modelType, modelId);
  const shared = useInteraction(key);

  // The server's value is authoritative; the optimistic one only outranks it
  // while the server is still reporting the pre-toggle value. Two cases this
  // resolves, in the same expression:
  //
  //  - the incoming prop still says what it said before the flip → it's stale
  //    (a not-yet-refetched list, or a public/anonymous SSR payload that reports
  //    `false` for everyone) or the request is still in flight → keep ours;
  //  - the incoming prop says something else → the server has moved on, for
  //    whatever reason, including a change made in another tab → take it.
  //
  // That bounds an optimistic value to the window before fresh data arrives,
  // rather than for the rest of the SPA session.
  const hasFreshServerLike = shared?.likeBase !== undefined && shared.likeBase !== initialLiked;
  const liked = shared?.like != null && !hasFreshServerLike ? shared.like : initialLiked;
  const hasFreshServerCount = shared?.likeCountBase !== undefined && shared.likeCountBase !== initialCount;
  const count = shared?.likeCount != null && !hasFreshServerCount ? shared.likeCount : initialCount;
  const isPending = shared?.likePending ?? false;
  const toggle = async () => {
    if (!user) {
      openLoginModal();
      return;
    }
    // Not addressable (no id yet) or a request for this exact model+action is
    // already out — guarded rather than `disabled`, because clicks on a
    // disabled button fall through to the card's wrapping <Link>.
    if (!key || isPending) return;
    const previous = getInteraction(key);
    const next = !liked;
    const nextCount = Math.max(0, next ? count + 1 : count - 1);
    setInteraction(key, {
      like: next,
      likeBase: initialLiked,
      likeCount: nextCount,
      likeCountBase: initialCount,
      likePending: true
    });
    try {
      await toggleLike(modelType, modelId!, next ? "like" : "remove");
      setInteraction(key, {
        likePending: false
      });
      // Write the confirmed result into any cached list/detail holding this
      // entity, so every card reading from the cache agrees and the optimistic
      // override above can retire.
      syncInteractionCache(queryClient, modelType, modelId!, {
        like: next,
        likeCount: nextCount
      });
    } catch (error) {
      // Both the flag and the count go back together. Only the like fields are
      // restored (`undefined` falls back to the props above) so a watchlist
      // action running concurrently on the same entity isn't clobbered.
      replaceInteraction(key, {
        ...getInteraction(key),
        like: previous?.like,
        likeBase: previous?.likeBase,
        likeCount: previous?.likeCount,
        likeCountBase: previous?.likeCountBase,
        likePending: false
      });
      toastApiError(error, errorMessage || "Failed to update like");
    }
  };
  return {
    liked,
    count,
    isPending,
    toggle
  };
}